import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { getAdventure, resolveAdventureImages } from "../content";
import type { AdventureOption } from "../types/adventure";
import type { ChallengeMode } from "./ChallengePanel";
import {
  gameReducer,
  INITIAL_STATE,
  isFinalRound,
  OUTCOME_MS,
  REVEAL_PAUSE_MS,
  createSummary,
} from "../lib/game-state";
import type { GameSummary } from "../types/adventure";
import { AdventureSelect } from "./AdventureSelect";
import { CrashScreen } from "./CrashScreen";
import { DebugPanel } from "./DebugPanel";
import { GameStage } from "./GameStage";
import { LessonScreen } from "./LessonScreen";
import { SummaryScreen } from "./SummaryScreen";

interface GameProps {
  adventures: ReturnType<typeof import("../content").getAdventures>;
  isPaid: boolean;
  portalUrl?: string;
  onPaywall: (feature: string) => void;
  forcedAdventureId?: string;
  shareMode?: boolean;
  onReachSummary?: (summary: GameSummary) => void;
}

export function Game({
  adventures,
  isPaid,
  portalUrl,
  onPaywall,
  forcedAdventureId,
  shareMode = false,
  onReachSummary,
}: GameProps) {
  const [showDebugPanel, setShowDebugPanel] = useState(false);
  const [showPositionDebug, setShowPositionDebug] = useState(false);
  const [showLessonPreview, setShowLessonPreview] = useState(false);

  const [state, dispatch] = useReducer(
    (s: typeof INITIAL_STATE, action: Parameters<typeof gameReducer>[1]) => {
      const adventure = s.adventureId ? getAdventure(s.adventureId) ?? null : null;
      return gameReducer(s, action, adventure);
    },
    INITIAL_STATE
  );
  const stateRef = useRef(state);
  stateRef.current = state;
  const summaryReportedRef = useRef(false);

  const adventure = state.adventureId ? getAdventure(state.adventureId) : null;

  useEffect(() => {
    if (!forcedAdventureId || state.phase !== "select") return;
    dispatch({ type: "SELECT_ADVENTURE", adventureId: forcedAdventureId });
  }, [forcedAdventureId, state.phase]);

  useEffect(() => {
    if (state.phase !== "summary" || !state.adventureId || !onReachSummary) return;
    if (summaryReportedRef.current) return;
    const adv = getAdventure(state.adventureId);
    if (!adv) return;
    summaryReportedRef.current = true;
    onReachSummary(createSummary(adv, state));
  }, [state, onReachSummary]);
  const previewAdventure =
    adventure ?? adventures.find((a) => a.id === "ruimtereis") ?? adventures[0] ?? null;

  const lessonPreviewOverlay =
    showLessonPreview && previewAdventure && state.phase !== "lesson" ? (
      <LessonScreen
        explanation={previewAdventure.actExplanation}
        onContinue={() => setShowLessonPreview(false)}
      />
    ) : null;
  const images = useMemo(
    () => (adventure ? resolveAdventureImages(adventure) : {}),
    [adventure]
  );

  const currentRound = adventure?.rounds[state.roundIndex];
  const roundNumber = currentRound?.n ?? 5;

  const handleChoose = useCallback(
    (option: AdventureOption) => {
      if (state.phase === "choose") {
        dispatch({
          type: "FINISH_CHOICE",
          optionId: option.id,
          best: option.best,
          outcome: option.outcome,
        });
      } else {
        dispatch({
          type: "CHOOSE_OPTION",
          optionId: option.id,
          best: option.best,
          outcome: option.outcome,
        });
      }
    },
    [state.phase]
  );

  const inActiveGame =
    state.phase === "playing" ||
    state.phase === "crash" ||
    state.phase === "lesson" ||
    state.phase === "defuse" ||
    state.phase === "choose";

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && !event.shiftKey && event.key.toLowerCase() === "d") {
        event.preventDefault();
        setShowDebugPanel((on) => !on);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  // ponytail: Playwright/social capture helper — only in Vite DEV
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    const api = {
      openDebug: () => setShowDebugPanel(true),
      jumpRound: (roundIndex: number) =>
        dispatch({ type: "DEBUG_JUMP_ROUND", roundIndex }),
      jumpPhase: (phase: "crash" | "lesson" | "defuse" | "choose") =>
        dispatch({ type: "DEBUG_JUMP_PHASE", phase }),
    };
    (window as Window & { __jariActCapture?: typeof api }).__jariActCapture = api;
    return () => {
      delete (window as Window & { __jariActCapture?: typeof api }).__jariActCapture;
    };
  }, []);

  // Final-round countdown (20s), only during the last challenge
  useEffect(() => {
    if (state.phase !== "playing" || !adventure) return;
    if (!isFinalRound(adventure, state.roundIndex)) return;
    if (state.stage !== "challenge") return;
    const id = window.setInterval(() => dispatch({ type: "TICK" }), 1000);
    return () => window.clearInterval(id);
  }, [state.phase, state.roundIndex, state.stage, adventure]);

  // Round sequencing: reveal -> challenge -> outcome -> advance
  useEffect(() => {
    if (state.phase !== "playing" || state.stage !== "reveal") return;
    if (state.revealedCount >= state.thoughts.length) return;
    dispatch({ type: "REVEAL_NEXT" });
  }, [state.phase, state.stage, state.roundIndex]);

  const handleThoughtSettled = useCallback((thoughtIndex: number) => {
    const current = stateRef.current;
    if (current.phase !== "playing" || current.stage !== "reveal") return;
    if (thoughtIndex !== current.revealedCount - 1) return;

    if (current.revealedCount < current.thoughts.length) {
      dispatch({ type: "REVEAL_NEXT" });
      return;
    }

    window.setTimeout(() => {
      const afterPause = stateRef.current;
      if (afterPause.phase === "playing" && afterPause.stage === "reveal") {
        dispatch({ type: "BEGIN_CHALLENGE" });
      }
    }, REVEAL_PAUSE_MS);
  }, []);

  useEffect(() => {
    if (state.phase !== "playing") return;

    if (state.stage === "outcome") {
      const id = window.setTimeout(
        () => dispatch({ type: "ADVANCE_ROUND" }),
        OUTCOME_MS
      );
      return () => window.clearTimeout(id);
    }
  }, [
    state.phase,
    state.stage,
  ]);

  if (state.phase === "select") {
    return (
      <>
        <AdventureSelect
          adventures={adventures}
          isPaid={isPaid}
          onSelect={(id) => dispatch({ type: "SELECT_ADVENTURE", adventureId: id })}
          onLocked={(label) => onPaywall(`het avontuur "${label}"`)}
        />
        {lessonPreviewOverlay}
      </>
    );
  }

  if (!adventure) return null;

  if (state.phase === "intro") {
    return (
      <>
        <section className="act-intro jari-card" style={adventure.theme as CSSProperties}>
          <span className="act-intro__icon" aria-hidden>
            {adventure.goal.icon}
          </span>
          <h2>{adventure.label}</h2>
          <p>{adventure.tagline}</p>
          {adventure.showIntroGoal !== false ? (
            <p className="act-intro__goal">
              Jouw doel: <strong>{adventure.goal.name}</strong>
            </p>
          ) : null}
          <p className="act-intro__hint">
            {adventure.introHint ??
              "Onderweg krijg je signalen van je systemen. Dat zijn je gedachten. Sommige helpen, andere niet. Luister vooral naar de helpende!"}
          </p>
          <button
            type="button"
            className="jari-btn jari-btn--primary"
            onClick={() => dispatch({ type: "START_GAME" })}
          >
            Start avontuur
          </button>
          {!shareMode ? (
            <button
              type="button"
              className="jari-btn"
              onClick={() => dispatch({ type: "BACK_TO_SELECT" })}
            >
              Terug
            </button>
          ) : null}
        </section>
        {lessonPreviewOverlay}
      </>
    );
  }

  if (state.phase === "summary") {
    return (
      <>
        <SummaryScreen
          adventure={adventure}
          images={images}
          onRestart={() => dispatch({ type: "BACK_TO_SELECT" })}
        />
        {lessonPreviewOverlay}
      </>
    );
  }

  const selectMode = state.phase === "defuse";
  const focusMode = state.phase === "choose";

  let challengeMode: ChallengeMode | null = null;
  let showChallengeOptions = false;
  if (state.phase === "playing") {
    if (state.stage === "reveal" || state.stage === "challenge") {
      challengeMode = "challenge";
      showChallengeOptions = state.stage === "challenge";
    } else if (state.stage === "outcome") {
      challengeMode = "outcome";
    }
  } else if (focusMode) {
    challengeMode = "choose";
    showChallengeOptions = true;
  }

  const challengeRound =
    focusMode || selectMode ? adventure.rounds[4] : currentRound;

  const showTimer =
    state.phase === "playing" &&
    state.stage === "challenge" &&
    !!adventure &&
    isFinalRound(adventure, state.roundIndex);

  return (
    <div className="act-game" style={adventure.theme as CSSProperties}>
      <GameStage
        adventure={adventure}
        images={images}
        thoughts={state.thoughts}
        revealedCount={state.revealedCount}
        roundNumber={roundNumber}
        phase={state.phase}
        timeLeft={state.timeLeft}
        showTimer={showTimer}
        inReveal={state.phase === "playing" && state.stage === "reveal"}
        onThoughtSettled={handleThoughtSettled}
        selectMode={selectMode}
        focusMode={focusMode}
        selectedHelpfulIds={state.selectedHelpfulIds}
        selectPrompt={selectMode ? adventure.defusePrompt : undefined}
        onToggleHelpful={(thoughtId) =>
          dispatch({ type: "TOGGLE_HELPFUL_SELECTION", thoughtId })
        }
        challenge={
          challengeMode && challengeRound
            ? {
                mode: challengeMode,
                text: challengeRound.challenge,
                options: challengeRound.options,
                blocked: state.buttonsBlocked,
                lastOutcome: state.lastOutcome,
                lastBest: state.lastBest,
                onChoose: handleChoose,
                showOptions: showChallengeOptions,
              }
            : undefined
        }
        showPositionDebug={showPositionDebug}
      />

      {state.phase === "crash" && (
        <CrashScreen
          title={adventure.crashTitle}
          text={adventure.crashText}
          onContinue={() => dispatch({ type: "CRASH_CONTINUE" })}
        />
      )}

      {state.phase === "lesson" && (
        <LessonScreen
          explanation={adventure.actExplanation}
          onContinue={() => dispatch({ type: "LESSON_CONTINUE" })}
        />
      )}

      {lessonPreviewOverlay}

      {showDebugPanel && inActiveGame && adventure && (
        <DebugPanel
          showLessonPreview={showLessonPreview}
          showPositionDebug={showPositionDebug}
          activeRoundIndex={state.phase === "playing" ? state.roundIndex : null}
          activePhase={
            state.phase === "crash" ||
            state.phase === "lesson" ||
            state.phase === "defuse" ||
            state.phase === "choose"
              ? state.phase
              : null
          }
          roundCount={adventure.rounds.length}
          onToggleLesson={() => setShowLessonPreview((on) => !on)}
          onTogglePosition={() => setShowPositionDebug((on) => !on)}
          onJumpRound={(roundIndex) =>
            dispatch({ type: "DEBUG_JUMP_ROUND", roundIndex })
          }
          onJumpPhase={(phase) => dispatch({ type: "DEBUG_JUMP_PHASE", phase })}
        />
      )}

      {!shareMode ? (
        <button
          type="button"
          className="act-game__quit jari-btn"
          onClick={() => dispatch({ type: "BACK_TO_SELECT" })}
        >
          Stoppen
        </button>
      ) : null}
    </div>
  );
}
